import type { LocationCoverages } from 'api/quote-marketplace';

export const HABITATIONAL_LIABILITY_LIMIT_OPTIONS = [
  { value: 200000, label: '$200,000' },
  { value: 300000, label: '$300,000' },
  { value: 500000, label: '$500,000' },
  { value: 1000000, label: '$1,000,000' },
  { value: 2000000, label: '$2,000,000' },
  { value: 3000000, label: '$3,000,000' },
  { value: 4000000, label: '$4,000,000' },
  { value: 5000000, label: '$5,000,000' },
];

export const RECOMMENDED_HABITATIONAL_LIABILITY_LIMIT = 2000000;
export const UNDERWRITING_APPROVAL_THRESHOLD = 1000000;
export const UNDERWRITING_APPROVAL_THRESHOLD_NS = 500000;

export const COVERAGE_DEDUCTIBLE_OPTIONS = [
  { value: 500, label: '$500' },
  { value: 1000, label: '$1,000' },
  { value: 1500, label: '$1,500' },
  { value: 2000, label: '$2,000' },
  { value: 2500, label: '$2,500' },
  { value: 3000, label: '$3,000' },
  { value: 5000, label: '$5,000' },
  { value: 7500, label: '$7,500' },
  { value: 10000, label: '$10,000' },
];

export interface HabitationalCoverageDefinition {
  key: keyof LocationCoverages;
  enabledKey?: keyof LocationCoverages;
  limitKey?: keyof LocationCoverages;
  deductibleKey?: keyof LocationCoverages;
  name: string;
}

export const BYLAW_COVERAGE: HabitationalCoverageDefinition = {
  key: 'bylaw_enabled',
  enabledKey: 'bylaw_enabled',
  limitKey: 'bylaw_limit_cad',
  deductibleKey: 'bylaw_deductible_cad',
  name: 'By-Law',
};

export const FIRE_DEPARTMENT_COVERAGE: HabitationalCoverageDefinition = {
  key: 'fire_department_enabled',
  enabledKey: 'fire_department_enabled',
  limitKey: 'fire_department_limit_cad',
  deductibleKey: 'fire_department_deductible_cad',
  name: 'Fire Department Charges',
};

export const IDENTITY_THEFT_COVERAGE: HabitationalCoverageDefinition = {
  key: 'identity_theft_enabled',
  enabledKey: 'identity_theft_enabled',
  limitKey: 'identity_theft_limit_cad',
  deductibleKey: 'identity_theft_deductible_cad',
  name: 'Identity Theft',
};

export const SEWER_BACKUP_COVERAGE: HabitationalCoverageDefinition = {
  key: 'sewer_backup_enabled',
  enabledKey: 'sewer_backup_enabled',
  limitKey: 'sewer_backup_limit_cad',
  deductibleKey: 'sewer_backup_deductible_cad',
  name: 'Sewer Backup',
};

export const GROUND_WATER_COVERAGE: HabitationalCoverageDefinition = {
  key: 'ground_water_enabled',
  enabledKey: 'ground_water_enabled',
  limitKey: 'ground_water_limit_cad',
  deductibleKey: 'ground_water_deductible_cad',
  name: 'Ground Water',
};

export const ABOVE_GROUND_WATER_COVERAGE: HabitationalCoverageDefinition = {
  key: 'above_ground_water_enabled',
  enabledKey: 'above_ground_water_enabled',
  limitKey: 'above_ground_water_limit_cad',
  deductibleKey: 'above_ground_water_deductible_cad',
  name: 'Above Ground Water',
};

export const OVERLAND_WATER_COVERAGE: HabitationalCoverageDefinition = {
  key: 'overland_water_enabled',
  enabledKey: 'overland_water_enabled',
  limitKey: 'overland_water_limit_cad',
  deductibleKey: 'overland_water_deductible_cad',
  name: 'Overland Water',
};

export const SERVICE_LINE_COVERAGE: HabitationalCoverageDefinition = {
  key: 'service_line_enabled',
  enabledKey: 'service_line_enabled',
  limitKey: 'service_line_limit_cad',
  deductibleKey: 'service_line_deductible_cad',
  name: 'Service Line',
};

export const MIDDLE_SECTION_COVERAGES: HabitationalCoverageDefinition[] = [
  BYLAW_COVERAGE,
  FIRE_DEPARTMENT_COVERAGE,
  IDENTITY_THEFT_COVERAGE,
];

export const WATER_COVERAGES: HabitationalCoverageDefinition[] = [
  SEWER_BACKUP_COVERAGE,
  GROUND_WATER_COVERAGE,
  ABOVE_GROUND_WATER_COVERAGE,
  OVERLAND_WATER_COVERAGE,
  SERVICE_LINE_COVERAGE,
];

export const createEmptyLocationCoverages = (): LocationCoverages => ({
  coverage_form_type: '',
  deductible_cad: null,
  grbc_enabled: true,
  single_limit_enabled: true,
  bylaw_enabled: true,
  bylaw_limit_cad: null,
  bylaw_deductible_cad: null,
  fire_department_enabled: true,
  fire_department_limit_cad: null,
  fire_department_deductible_cad: null,
  identity_theft_enabled: true,
  identity_theft_limit_cad: null,
  identity_theft_deductible_cad: null,
  sewer_backup_enabled: true,
  sewer_backup_limit_cad: null,
  sewer_backup_deductible_cad: null,
  ground_water_enabled: true,
  ground_water_limit_cad: null,
  ground_water_deductible_cad: null,
  above_ground_water_enabled: true,
  above_ground_water_limit_cad: null,
  above_ground_water_deductible_cad: null,
  overland_water_enabled: true,
  overland_water_limit_cad: null,
  overland_water_deductible_cad: null,
  backflow_valve_enabled: false,
  sump_pump_pit_enabled: false,
  alarmed_sump_pump_enabled: false,
  service_line_enabled: true,
  service_line_limit_cad: null,
  service_line_deductible_cad: null,
  liability_limit_cad: 2000000,
  environmental_deductible_cad: null,
  earthquake_enabled: false,
  earthquake_coverage_option: '',
  earthquake_limit_cad: null,
  earthquake_condo_enabled: false,
  earthquake_condo_limit_cad: null,
  collapse_enabled: false,
  collapse_limit_cad: null,
  mass_evacuation_enabled: false,
  mass_evacuation_limit_cad: null,
  home_based_business_enabled: false,
  home_based_business_type: '',
  home_based_business_gross_annual_income: null,
  home_based_business_num_employees: null,
  home_based_business_products_outside_canada: false,
  home_based_business_internet_sales: false,
  home_based_business_professional_activity: false,
  home_based_business_coverage_enabled: false,
  glass_buydown_enabled: false,
  residential_glass_enabled: false,
  personal_cyber_enabled: false,
  food_freezer_enabled: false,
  malicious_damage_enabled: false,
  residence_burglary_enabled: false,
  theft_and_burglary_enabled: false,
  vandalism_on_dwelling_enabled: false,
  vandalism_theft_by_tenants_enabled: false,
  rental_income_enabled: false,
  rental_income_limit_cad: null,
  rental_income_deductible_cad: null,
  personal_cyber_limit_cad: null,
  personal_cyber_deductible_cad: null,
  solar_panel_enabled: false,
  solar_panel_date_of_installation: null,
  solar_panel_installation_location: '',
  solar_panel_type: '',
  solar_panel_capacity: null,
  solar_panel_tracking: false,
  solar_panel_leased: false,
  solar_panel_tied_to_grid: false,
  solar_panel_limit_cad: null,
  solar_panel_deductible_cad: null,
  solar_panel_settlement_basis: '',
  swimming_pool_enabled: false,
  swimming_pool_type: '',
  swimming_pool_material: '',
  swimming_pool_year_built: null,
  swimming_pool_fenced: null,
  property_in_storage_enabled: false,
  property_in_storage_include_burglary: false,
  property_in_storage_coverage_type: '',
  property_in_storage_value_cad: null,
  property_in_storage_percent_to_insure: null,
  hail_peril_adjustment_enabled: false,
  hail_peril_adjustment_limit_cad: null,
  hail_peril_adjustment_deductible_cad: null,
  wind_peril_adjustment_enabled: false,
  wind_peril_adjustment_limit_cad: null,
  wind_peril_adjustment_deductible_cad: null,
  water_peril_adjustment_enabled: false,
  water_peril_adjustment_limit_cad: null,
  water_peril_adjustment_deductible_cad: null,
  claim_free_protection_endorsement: false,
});
